import React, { useEffect, useState } from 'react';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import ApiService from "../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link, useSearchParams  } from 'react-router-dom';
import { getOrderStatus } from '../../utils/statusHelper';
import { formatCurrency, formatDate, formatDateLocal } from '../../utils/helper';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const List = () => {
    
    const [isLoader, setIsLoader] = useState(false);
    const [orders, setOrders] = useState([]);
    const [pagination, setPagination] = useState({});
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
  
    const [searchParams] = useSearchParams();
    
    const [apiData, setApiData] = useState({
        search: "",
        status: "",
        order_type: "",
        waybill_number: "",
        start_date: "",
        end_date: "",
        page: 1,
    });

    useEffect(() => {
        getOrders();
    }, [apiData.page, apiData.search, apiData.status, apiData.order_type, apiData.waybill_number, apiData.start_date, apiData.end_date]); 

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    }; 

    const getOrders = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/orders", 
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setOrders(data.data.orders || []);
                setPagination(data.data.pagination || {});
            }
        } catch (error) {
            console.error(error);
            // toast.error("Failed to fetch orders");
        } finally {
            setIsLoader(false);
        }
    };

    const handleCancelOrder = async (order) => {
        Swal.fire({
            title: 'Cancel Order?',
            text: `Are you sure you want to cancel order ${order.ref_no}? Stock will be restored and order will be marked as cancelled.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, cancel it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setIsLoader(true);
                    const response = await ApiService.request({
                        method: "PUT",
                        url: `admin/orders/${order.id}`,
                        data: { 
                            status: 'cancelled',
                            amount_paid: order.amount_paid,
                            payment_status: order.payment_status
                        }
                    });
                    if (response.data.status) {
                        toast.success("Order cancelled and stock restored successfully");
                        getOrders();
                    } else {
                        toast.error(response.data.message);
                    }
                } catch (error) {
                    toast.error(error.response?.data?.message || "Failed to cancel order");
                } finally {
                    setIsLoader(false);
                }
            }
        });
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await ApiService.request({
                        method: "DELETE",
                        url: `admin/orders/${id}`
                    });
                    if (response.data.status) {
                        toast.success(response.data.message);
                        getOrders();
                    } else {
                        toast.error(response.data.message);
                    }
                } catch (error) {
                    toast.error(error.response?.data?.message || "Failed to delete order");
                }
            }
        });
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Order List
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Create Order</Tooltip>}>
                            <Link to="/orders/create" className="btn btn-sm fw-bold btn-base">Create Order</Link>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1 me-3">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor" />
                                            <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor" />
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        data-kt-user-table-filter="search"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search order"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="w-200px">
                                        <select
                                            className="form-select form-select-solid"
                                            value={apiData.status}
                                            onChange={(e) => setApiData({ ...apiData, status: e.target.value })}
                                        >
                                            <option value="">All Status</option>
                                            <option value="pending">Pending</option>
                                            <option value="ready_not_collected">Ready NOT collected</option>
                                            <option value="completed">Complete (collected)</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div className="w-200px">
                                        <select
                                            className="form-select form-select-solid"
                                            value={apiData.order_type}
                                            onChange={(e) => setApiData({ ...apiData, order_type: e.target.value, waybill_number: e.target.value === 'Waybill Order' ? apiData.waybill_number : "" })}
                                        >
                                            <option value="">All Order Types</option>
                                            <option value="Local Order">Local Order</option>
                                            <option value="Waybill Order">Waybill Order</option>
                                        </select>
                                    </div>
                                    {apiData.order_type === 'Waybill Order' && (
                                        <div className="w-200px">
                                            <input
                                                type="text"
                                                className="form-control form-control-solid"
                                                placeholder="Waybill Number"
                                                value={apiData.waybill_number}
                                                onChange={(e) => setApiData({ ...apiData, waybill_number: e.target.value })}
                                            />
                                        </div>
                                    )}
                                    <div className="w-250px ms-3">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            {isLoader ? (
                                <Loader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th className="min-w-125px">Ref No</th>
                                                <th className="min-w-125px">Date</th>
                                                {/* <th className="min-w-100px">Order ID</th> */}
                                                <th className="min-w-125px">Customer</th>
                                                <th className="min-w-125px">Amount</th>
                                                <th className="min-w-125px">Paid</th>
                                                <th className="min-w-125px">Status</th>
                                                <th className="min-w-125px">Ready Date</th>
                                                <th className="text-end min-w-100px">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {orders.map((order, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <span className="text-gray-800 fw-bold">{order.ref_no || '-'}</span> <br />
                                                        {order.waybill_number && (
                                                            <span className="text-muted fs-7">WB: {order.waybill_number}</span>
                                                        )}
                                                    </td>
                                                    <td>{formatDate(order.created_at)}</td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            <span className="text-gray-800 mb-1">
                                                                {order.customer_name}
                                                            </span>
                                                            <span>{order.customer_email}</span>
                                                        </div>
                                                    </td>
                                                    <td>{formatCurrency(order.total_amount)}</td>
                                                    <td>
                                                        <div className="">
                                                            <span className="text-gray-800 fw-bold">{formatCurrency(order.amount_paid || 0)}</span> <br />
                                                            <span className={`badge badge-light-${order.payment_status === 'paid' ? 'success' : order.payment_status === 'partially_paid' ? 'warning' : 'danger'} fw-bold fs-9`}>
                                                                {order.payment_status ? order.payment_status.replace('_', ' ').toUpperCase() : 'PENDING'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const { badgeClass, label } = getOrderStatus(order.status);
                                                            return (
                                                                <span className={`badge ${badgeClass}`}>
                                                                    {label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td>{order.ready_at ? formatDate(order.ready_at) : '-'}</td>
                                                    <td className="text-end">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>View Order Details</Tooltip>}>
                                                            <Link to={`/orders/${order.id}`} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-eye"></i>
                                                            </Link>
                                                        </OverlayTrigger>
                                                        {order.status !== 'cancelled' && (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Cancel Order</Tooltip>}>
                                                                <button onClick={() => handleCancelOrder(order)} className="btn btn-icon btn-light btn-active-light-warning btn-sm me-2">
                                                                    <i className="fas fa-ban"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                        )}
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete Order</Tooltip>}>
                                                            <button onClick={() => handleDelete(order.id)} className="btn btn-icon btn-light btn-active-light-danger btn-sm">
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            ))}
                                            {orders.length === 0 && (
                                                <tr>
                                                    <td colSpan="10" className="text-center">No orders found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <Paginator pagination={pagination} onPageChange={(page) => setApiData({ ...apiData, page })} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
